'use client';

import { useId } from 'react';
import { BlockRoot } from '@akademi/site-builder-runtime';
import { Icon } from '../shared/Icon';
import {
  Emphasised,
  items,
  SkImage,
  SkLink,
  surfaceClass,
  text,
  type Localized,
  type SkImageRef,
  type Surface,
} from '../shared/primitives';
import { SkBaseStyles, SkStyles } from '../styles/SkStyles';

/**
 * Opening section for an inner page (Kitchens, Doors & fronts, About, ...): the
 * page's single `<h1>`, a short introduction, up to two actions and an optional
 * leaf-masked image.
 *
 * <p>With no image it is a quiet text header on the warm surface, which is how
 * the functional pages (basket, account) could open if an operator adds one.
 */
export interface SpatialKitchenPageIntroAction {
  label?: Localized;
  href?: string;
  variant?: 'primary' | 'secondary' | 'text';
}

export interface SpatialKitchenPageIntroProps {
  eyebrow?: Localized;
  title?: Localized;
  emphasis?: Localized;
  intro?: Localized;
  image?: SkImageRef;
  actions?: SpatialKitchenPageIntroAction[];
  surface?: Surface;
}

const CSS = `
.sk-intro{padding-block:clamp(40px,4vw + 1rem,88px)}
.sk-intro__grid{display:grid;gap:2rem;align-items:center}
.sk-intro__text{display:grid;gap:1.25rem;max-width:40rem}
.sk-intro__actions{display:flex;flex-wrap:wrap;align-items:center;gap:.75rem 1.5rem;margin-top:.5rem}
.sk-intro__media{aspect-ratio:4/3;box-shadow:0 16px 40px rgba(32,38,34,.12)}
@media (min-width:769px){.sk-intro__grid--media{grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:clamp(2.5rem,5vw,5rem)}}
`;

export function SpatialKitchenPageIntro(props: SpatialKitchenPageIntroProps) {
  const title = text(props.title, 'Untitled page');
  const actions = items<SpatialKitchenPageIntroAction>(props.actions).slice(0, 2);
  const hasImage = Boolean(props.image?.src);
  const titleId = useId();

  return (
    <BlockRoot
      as="section"
      className={`sk sk-intro ${surfaceClass(props.surface, 'warm')}`}
      aria-labelledby={titleId}
    >
      <SkBaseStyles />
      <SkStyles id="page-intro" css={CSS} />
      <div className="sk-container">
        <div className={`sk-intro__grid${hasImage ? ' sk-intro__grid--media' : ''}`}>
          <div className="sk-intro__text">
            {text(props.eyebrow) && <p className="sk-eyebrow">{text(props.eyebrow)}</p>}
            <h1 id={titleId} className="sk-heading sk-h1">
              <Emphasised title={title} emphasis={text(props.emphasis)} />
            </h1>
            {text(props.intro) && <p className="sk-lead">{text(props.intro)}</p>}
            {actions.length > 0 && (
              <div className="sk-intro__actions">
                {actions.map((action, index) => {
                  const label = text(action.label);
                  if (!label || !action.href) return null;
                  if (action.variant === 'text') {
                    return (
                      <SkLink key={index} href={action.href} className="sk-textlink">
                        {label}
                        <Icon name="arrow-right" size={18} />
                      </SkLink>
                    );
                  }
                  return (
                    <SkLink
                      key={index}
                      href={action.href}
                      className={`sk-btn ${action.variant === 'secondary' ? 'sk-btn--secondary' : 'sk-btn--primary'}`}
                    >
                      {label}
                    </SkLink>
                  );
                })}
              </div>
            )}
          </div>
          {hasImage && (
            <div className="sk-intro__media sk-media sk-leaf">
              <SkImage image={props.image} priority sizes="(min-width: 769px) 50vw, 100vw" />
            </div>
          )}
        </div>
      </div>
    </BlockRoot>
  );
}
